import { z } from "zod";
import { IsoDateTime, Uuid, withoutLink } from "./common";

/** Strongest first. Each role can do everything the roles after it can. */
export const ROLES = ["owner", "editor", "approver", "viewer"] as const;
export const Role = z.enum(ROLES);
export type Role = z.infer<typeof Role>;

export const UI_LANGS = ["ro", "bg", "hu", "en"] as const;
export const UiLang = z.enum(UI_LANGS);
export type UiLang = z.infer<typeof UiLang>;

export const BILLING_MODES = ["free", "stripe", "host"] as const;
export const BillingMode = z.enum(BILLING_MODES);
export type BillingMode = z.infer<typeof BillingMode>;

export const TENANT_STATUSES = ["active", "suspended"] as const;
export const TenantStatus = z.enum(TENANT_STATUSES);
export type TenantStatus = z.infer<typeof TenantStatus>;

export const Country = z.string().regex(/^[A-Z]{2}$/, "ISO 3166-1 alpha-2");
export const Language = z.string().regex(/^[a-z]{2}$/, "ISO 639-1");
export const Vertical = z.string().regex(/^[a-z][a-z0-9-]{0,39}$/);

export const Timezone = z.string().refine((tz) => {
  try {
    new Intl.DateTimeFormat("en", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}, "unknown IANA timezone");

/** Theme from the SSO token or host white-label. `tokens` are CSS variable overrides without the `--` prefix. */
export const Theme = z.object({
  mode: z.enum(["light", "dark", "system"]).optional(),
  tokens: z.record(z.string().regex(/^[a-z][a-z0-9-]*$/), z.string().max(200)).optional(),
});
export type Theme = z.infer<typeof Theme>;

const Hostname = z.string().max(255).regex(/^(?=.{1,255}$)([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/, "domain_invalid");

export const WhiteLabel = z.object({
  name: z.string().trim().max(120).optional(),
  /** Where this host's people open the app. Links in letters point here instead of the service's own address. */
  domain: Hostname.optional(),
  /** The same for the public API pages: unsubscribe, forms, short links. It has to be pointed at the API first. */
  apiDomain: Hostname.optional(),
  logoUrl: z.url().optional(),
  theme: Theme.optional(),
  /** The address the service's own letters come from. Its domain has to be verified with the mail provider. */
  senderEmail: z.email().optional(),
  supportEmail: z.email().optional(),
  /** People sign up and sign in here with an e-mail, rather than arriving from the host's own login. */
  directAccounts: z.boolean().optional(),
});
export type WhiteLabel = z.infer<typeof WhiteLabel>;

export const TenantDto = z.object({
  id: Uuid,
  hostId: Uuid,
  agencyId: Uuid.nullable(),
  /** A host's system feeds this tenant: what it knows and who its people are is read-only here. */
  fedByHost: z.boolean(),
  hostRef: z.string(),
  name: z.string(),
  country: z.string(),
  languages: z.array(z.string()),
  timezone: z.string(),
  vertical: z.string(),
  billingMode: BillingMode,
  status: TenantStatus,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type TenantDto = z.infer<typeof TenantDto>;

/** `status` is accepted from host keys only. `billingMode` stays in the shape to be refused by name: only a platform operator changes it. */
export const TenantPatch = z
  .object({
    name: withoutLink(z.string().trim().min(1).max(200)),
    country: Country,
    languages: z.array(Language).min(1).max(8),
    timezone: Timezone,
    vertical: Vertical,
    billingMode: BillingMode,
    status: TenantStatus,
  })
  .partial()
  .strict();
export type TenantPatch = z.infer<typeof TenantPatch>;

export const ActorDto = z.object({
  kind: z.enum(["user", "api_key", "system", "platform"]),
  id: z.string(),
  name: z.string(),
  role: Role,
});
export type ActorDto = z.infer<typeof ActorDto>;

export const MeResponse = z.object({
  actor: ActorDto,
  user: z.object({ id: Uuid, name: z.string(), email: z.string().nullable() }).nullable(),
  host: z.object({ id: Uuid, slug: z.string(), name: z.string(), whiteLabel: WhiteLabel }),
  tenant: TenantDto.nullable(),
  /** From the session token; null for API keys. */
  lang: UiLang.nullable(),
  theme: Theme.nullable(),
  agencies: z.array(
    z.object({
      id: Uuid,
      name: z.string(),
      role: Role,
      tenants: z.array(z.object({ id: Uuid, name: z.string(), hostRef: z.string() })),
    }),
  ),
});
export type MeResponse = z.infer<typeof MeResponse>;
